import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Tag, Typography, Space, Button, Modal, Select, Input, message, Alert,
} from 'antd';
import {
  CheckCircleOutlined,
  UserOutlined,
  CheckOutlined,
  StopOutlined,
  CrownOutlined,
  RetweetOutlined,
} from '@ant-design/icons';
import type { ReviewGateDto } from '@/types/review';
import type { User, UserRole } from '@/types/common';
import { reviewApi } from '@/api/reviewApi';
import { userApi } from '@/api/userApi';
import { useAuth } from '@/auth/useAuth';
import dayjs from 'dayjs';

const { Text } = Typography;
const { TextArea } = Input;

const ROLE_LABELS: Record<UserRole, string> = {
  CEO: 'CEO', BD_MANAGER: 'BD Manager', ENTERPRISE_ARCHITECT: 'Enterprise Architect',
  CLOUD_ARCHITECT: 'Cloud Architect', OPPORTUNITY_FINDER: 'Opportunity Finder',
  REQUIREMENT_RESOURCING_MGR: 'Resourcing Manager', TECHNICAL_WRITER: 'Technical Writer',
  QC_LEAD: 'QC Lead', CONTRIBUTOR: 'Contributor', ADMIN: 'Admin',
};

interface CeoSignOffProps {
  opportunityId: string;
  reviewGates: ReviewGateDto[];
  onComplete: () => void;
}

const CeoSignOff: React.FC<CeoSignOffProps> = ({ opportunityId, reviewGates, onComplete }) => {
  const { hasAnyRole } = useAuth();
  const isCeoOrAdmin = hasAnyRole(['CEO', 'ADMIN']);

  const [ceoFeedback, setCeoFeedback] = useState('');
  const [ceoSubmitting, setCeoSubmitting] = useState(false);
  const [ceoAction, setCeoAction] = useState<'approve' | 'reject' | null>(null);
  const [reassignVisible, setReassignVisible] = useState(false);
  const [reassignUserId, setReassignUserId] = useState<string | undefined>();
  const [reassigning, setReassigning] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const executiveGate = reviewGates.find((g) => g.gateType === 'EXECUTIVE');
  const parallelGates = reviewGates.filter((g) => g.gateType !== 'EXECUTIVE');
  const allParallelApproved = parallelGates.length > 0 && parallelGates.every((g) => g.status === 'APPROVED');
  const ceoApproved = executiveGate?.status === 'APPROVED';
  const ceoRejected = executiveGate?.status === 'REJECTED';

  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const response = await userApi.getAll({ size: 100, isActive: true });
      setUsers(response.content || []);
    } catch {
      message.error('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const handleReassign = async () => {
    if (!executiveGate || !reassignUserId) {
      message.warning('Please select a user');
      return;
    }
    setReassigning(true);
    try {
      await reviewApi.changeGateOwner(opportunityId, executiveGate.id, reassignUserId);
      message.success('CEO executive review reassigned');
      setReassignVisible(false);
      setReassignUserId(undefined);
      onComplete();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      message.error(err?.response?.data?.message || 'Failed to reassign');
    } finally {
      setReassigning(false);
    }
  };

  const handleApprove = async () => {
    if (!executiveGate) return;
    setCeoSubmitting(true);
    try {
      await reviewApi.approveGate(opportunityId, executiveGate.id, ceoFeedback.trim() || undefined);
      message.success('Executive sign-off approved');
      setCeoFeedback('');
      setCeoAction(null);
      onComplete();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      message.error(err?.response?.data?.message || 'Failed to approve');
    } finally {
      setCeoSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!executiveGate) return;
    if (!ceoFeedback.trim()) {
      message.warning('Please provide a reason for rejection');
      return;
    }
    setCeoSubmitting(true);
    try {
      await reviewApi.rejectGate(opportunityId, executiveGate.id, ceoFeedback.trim());
      message.success('Executive sign-off rejected');
      setCeoFeedback('');
      setCeoAction(null);
      onComplete();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      message.error(err?.response?.data?.message || 'Failed to reject');
    } finally {
      setCeoSubmitting(false);
    }
  };

  if (!executiveGate) {
    return (
      <Card size="small" style={{ marginTop: 12, borderRadius: 8 }}>
        <Space>
          <CrownOutlined style={{ color: 'var(--color-text-tertiary)' }} />
          <Text type="secondary" style={{ fontSize: 13 }}>
            Executive sign-off (E.4) unlocks once the review gates are initialized — use
            “Initialize Review Gates” in the Review Gates section below.
          </Text>
        </Space>
      </Card>
    );
  }

  return (
    <>
      <Card
        size="small"
        style={{
          marginTop: 12,
          marginBottom: 12,
          borderRadius: 8,
          borderColor: ceoApproved ? 'var(--color-success)' : ceoRejected ? 'var(--color-error)' : 'var(--color-warning)',
          background: ceoApproved ? 'var(--color-success-light)' : ceoRejected ? 'var(--color-error-light)' : 'var(--color-warning-light)',
        }}
        title={
          <Space>
            <CrownOutlined style={{ color: ceoApproved ? 'var(--color-success)' : '#d48806' }} />
            <Text strong style={{ fontSize: 14 }}>CEO Executive Sign-off</Text>
            {ceoApproved && <Tag color="success" icon={<CheckCircleOutlined />}>Approved</Tag>}
            {ceoRejected && <Tag color="error">Rejected</Tag>}
            {!ceoApproved && !ceoRejected && (
              <Tag color="warning">Awaiting CEO Approval</Tag>
            )}
            {isCeoOrAdmin && !ceoApproved && (
              <Button size="small" icon={<RetweetOutlined />} onClick={(e) => { e.stopPropagation(); fetchUsers(); setReassignVisible(true); }}>
                Reassign
              </Button>
            )}
          </Space>
        }
      >
        {(ceoApproved || ceoRejected) && (
          <Space direction="vertical" style={{ width: '100%' }} size={4}>
            <Space>
              <UserOutlined />
              <Text style={{ fontSize: 12 }}>
                {executiveGate.reviewer || 'CEO'}
              </Text>
              {executiveGate.completedAt && (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  — {dayjs(executiveGate.completedAt).format('MMM D, YYYY h:mm A')}
                </Text>
              )}
            </Space>
            {executiveGate.feedback && (
              <div
                style={{
                  padding: '6px 10px',
                  background: ceoApproved ? 'var(--color-success-light)' : 'var(--color-error-light)',
                  borderLeft: `3px solid ${ceoApproved ? 'var(--color-success)' : 'var(--color-error)'}`,
                  borderRadius: 4,
                  marginTop: 4,
                }}
              >
                <Text italic style={{ fontSize: 12 }}>
                  &ldquo;{executiveGate.feedback}&rdquo;
                </Text>
              </div>
            )}
          </Space>
        )}

        {!ceoApproved && !ceoRejected && (
          <div>
            {!allParallelApproved && (
              <Alert
                message="All Phase E reviews (Technical, Strategy, Compliance) must be approved before CEO sign-off."
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
              />
            )}

            {allParallelApproved && !isCeoOrAdmin && (
              <Alert
                message="Awaiting CEO or Admin to approve or reject."
                type="info"
                showIcon
              />
            )}

            {allParallelApproved && isCeoOrAdmin && (
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Text type="secondary" style={{ fontSize: 12 }}>
                  All Phase E reviews are approved. Review the pipeline summary and provide
                  your decision on this proposal submission.
                </Text>
                <div>
                  <Text strong style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>
                    Feedback / Notes {ceoAction === 'reject' && <span style={{ color: 'var(--color-error)' }}>*</span>}
                  </Text>
                  <TextArea
                    value={ceoFeedback}
                    onChange={(e) => setCeoFeedback(e.target.value)}
                    placeholder={ceoAction === 'reject'
                      ? 'Provide reason for rejection (required)...'
                      : 'Optional approval notes...'}
                    rows={3}
                  />
                </div>
                <Space>
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    onClick={() => { setCeoAction('approve'); handleApprove(); }}
                    loading={ceoSubmitting && ceoAction === 'approve'}
                    disabled={ceoSubmitting}
                  >
                    Approve for Submission
                  </Button>
                  <Button
                    danger
                    icon={<StopOutlined />}
                    onClick={() => {
                      if (!ceoFeedback.trim()) {
                        setCeoAction('reject');
                        message.info('Please provide a reason for rejection, then click Reject again');
                        return;
                      }
                      setCeoAction('reject');
                      handleReject();
                    }}
                    loading={ceoSubmitting && ceoAction === 'reject'}
                    disabled={ceoSubmitting}
                  >
                    Reject
                  </Button>
                </Space>
              </Space>
            )}
          </div>
        )}
      </Card>

      <Modal
        title="Reassign CEO Executive Review"
        open={reassignVisible}
        onCancel={() => setReassignVisible(false)}
        onOk={handleReassign}
        confirmLoading={reassigning}
        okText="Reassign"
      >
        <Text style={{ display: 'block', marginBottom: 12 }}>
          Select a user to assign the executive sign-off to.
        </Text>
        <Select
          style={{ width: '100%' }}
          placeholder="Select a user"
          value={reassignUserId}
          onChange={setReassignUserId}
          showSearch
          optionFilterProp="label"
          loading={loadingUsers}
          options={users.map((u) => ({
            value: u.id,
            label: `${u.fullName} — ${ROLE_LABELS[u.role] || u.role}${u.secondaryRole ? ` / ${ROLE_LABELS[u.secondaryRole] || u.secondaryRole}` : ''}`,
          }))}
        />
      </Modal>
    </>
  );
};

export default CeoSignOff;
